import React, { useState } from 'react';
import PropTypes from 'prop-types';
import IconButton from '@material-ui/core/IconButton';
import Collapse from '@material-ui/core/Collapse';
import Avatar from '@material-ui/core/Avatar';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
    root: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 4px',
        cursor: 'pointer',
        minHeight: 36,
        borderRadius: 6,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    avatar: {
        width: 24,
        height: 24,
        fontSize: '0.7rem',
        flexShrink: 0,
    },
    preview: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
    },
    title: {
        fontSize: '0.78rem',
        fontWeight: 600,
        lineHeight: 1.25,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: theme.palette.text.primary,
    },
    meta: {
        fontSize: '0.65rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.2,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        gap: 0,
    },
    iconBtn: {
        padding: 4,
        width: 28,
        height: 28,
    },
    body: {
        padding: '0 8px 8px 36px',
    },
    bodyText: {
        fontSize: '0.78rem',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.action.hover,
        borderRadius: 6,
        padding: '6px 8px',
        lineHeight: 1.35,
    },
}));

export default function ContactNotesDialogListItem(props) {
    const { note, deleteItem } = props;
    const classes = useStyles();
    const [expanded, setExpanded] = useState(false);

    const handleDelete = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (note && deleteItem) {
            deleteItem(note);
        }
    };

    const preview =
        (note?.note || '').length > 48
            ? `${(note.note || '').slice(0, 48)}…`
            : (note?.note || '');

    const userName = note?.user?.name || '—';
    const initial = (userName || '?').charAt(0).toUpperCase();

    return (
        <div className={classes.root}>
            <div
                className={classes.row}
                onClick={() => setExpanded((prev) => !prev)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setExpanded((prev) => !prev);
                    }
                }}
            >
                <Avatar className={classes.avatar} alt={userName}>
                    {initial}
                </Avatar>
                <div className={classes.preview}>
                    <Typography className={classes.title} title={note?.note || ''}>
                        {preview || '(sem texto)'}
                    </Typography>
                    <Typography className={classes.meta}>
                        {userName} · {moment(note.createdAt).format('DD/MM/YY HH:mm')}
                    </Typography>
                </div>
                <div className={classes.actions}>
                    <Tooltip title={expanded ? 'Recolher' : 'Ver conteúdo'}>
                        <IconButton
                            className={classes.iconBtn}
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setExpanded((prev) => !prev);
                            }}
                            aria-label={expanded ? 'recolher' : 'expandir'}
                        >
                            {expanded ? (
                                <ExpandLessIcon fontSize="small" />
                            ) : (
                                <ExpandMoreIcon fontSize="small" />
                            )}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                        <IconButton
                            className={classes.iconBtn}
                            size="small"
                            onClick={handleDelete}
                            aria-label="delete"
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </div>
            </div>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <div className={classes.body}>
                    <Typography className={classes.bodyText}>
                        {note?.note || ''}
                    </Typography>
                </div>
            </Collapse>
        </div>
    );
}

ContactNotesDialogListItem.propTypes = {
    note: PropTypes.object.isRequired,
    deleteItem: PropTypes.func.isRequired,
    editItem: PropTypes.func,
};
